import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Fade } from '@mui/material';
import Login from './Login';
import Register from './Register';
import EmailVerification from './EmailVerification';
import ReviewsSection from './ReviewsSection';
import { resolvePostAuthRedirect } from '../utils/authRedirect';
import { persistSignupPostAuthRedirect } from '../utils/signupAuthRedirect';

const AuthWrapper = ({ onAuthSuccess, onBack }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialView = (location.pathname === '/register' || location.pathname === '/signup') ? 'register' : 'login';
  const initialReferralCode = searchParams.get('ref') || '';
  const [currentView, setCurrentView] = useState(initialView);
  const [fadeKey, setFadeKey] = useState(0);
  const [pendingUser, setPendingUser] = useState(null);
  const [devEmailHint, setDevEmailHint] = useState(null);
  const postAuthRedirect = resolvePostAuthRedirect(location.search);

  useEffect(() => {
    const view = (location.pathname === '/register' || location.pathname === '/signup') ? 'register' : 'login';
    setCurrentView(view);
  }, [location.pathname]);

  useEffect(() => {
    persistSignupPostAuthRedirect(location.search);
  }, [location.search]);

  const switchToRegister = () => {
    navigate(`/signup${location.search}`, { replace: true });
  };

  const switchToLogin = () => {
    navigate(`/login${location.search}`, { replace: true });
  };

  const handleRegistrationSuccess = (user, emailVerificationEnabled = true, nextDevEmailHint = null) => {
    if (!emailVerificationEnabled || user.email_verified) {
      onAuthSuccess && onAuthSuccess(user);
    } else {
      setPendingUser(user);
      setDevEmailHint(nextDevEmailHint);
      setFadeKey((prev) => prev + 1);
      setTimeout(() => setCurrentView('verify-email'), 150);
    }
  };

  const handleLoginSuccess = (user) => {
    if (user === null) {
      onAuthSuccess && onAuthSuccess(user);
      return;
    }

    if (!user.email_verified) {
      setPendingUser(user);
      setDevEmailHint(null);
      setFadeKey((prev) => prev + 1);
      setTimeout(() => setCurrentView('verify-email'), 150);
    } else {
      onAuthSuccess && onAuthSuccess(user);
    }
  };

  const handleEmailVerificationSuccess = (user) => {
    onAuthSuccess && onAuthSuccess(user);
  };

  const handleBackToLogin = () => {
    setPendingUser(null);
    setDevEmailHint(null);
    setFadeKey((prev) => prev + 1);
    setTimeout(() => setCurrentView('login'), 150);
  };

  return (
    <Box>
      <Fade in={true} key={fadeKey} timeout={300}>
        <Box>
          {currentView === 'login' && (
            <Login
              onSuccess={handleLoginSuccess}
              switchToRegister={switchToRegister}
              onBack={onBack}
              postAuthRedirect={postAuthRedirect}
            />
          )}
          {currentView === 'register' && (
            <Register
              onSuccess={handleRegistrationSuccess}
              switchToLogin={switchToLogin}
              initialReferralCode={initialReferralCode}
              postAuthRedirect={postAuthRedirect}
            />
          )}
          {currentView === 'verify-email' && pendingUser && (
            <EmailVerification
              user={pendingUser}
              devEmailHint={devEmailHint}
              onSuccess={handleEmailVerificationSuccess}
              onBackToLogin={handleBackToLogin}
            />
          )}
        </Box>
      </Fade>

      {(currentView === 'login' || currentView === 'register') && (
        <ReviewsSection
          title="Trusted by Job Seekers Worldwide"
          subtitle="See what our users say about their JobMockAI experience"
          maxReviews={6}
          showViewAll={false}
        />
      )}
    </Box>
  );
};

export default AuthWrapper;
